import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import parse from 'html-react-parser';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';

import { translate as t } from 'hooks/useTranslations';
import { ServerErrorResponse } from 'data_sources/constants';
import errorHelper from 'helpers/errorHelper';
import useDraftManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';
import useAcknowledgementSettings from './_data_sources/useAcknowledgementSettings';
import useManageAcknowledgementSettings from './_data_sources/useManageAcknowledgementSetting';
import { DraftManual } from 'views/App/Portal/Policy/_types/manuals/draft';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Close as RemoveIcon } from '@mui/icons-material';
import { neutral, success } from '_lex/_foundations/phxColors';
import { type AcknowledgementSettings as AcknowledgmentSettingsType } from 'views/App/Portal/Policy/_types/acknowledgements';
import { useInView } from 'react-intersection-observer';
import useIssuedManualGroups, {
  ManualGroup,
} from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManualGroups';
import { QuickSearch } from 'components/_atoms';
import Loading from 'views/App/Portal/Loading';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { Form } from 'components/_forms';
import TruncatedText from '_lex/_atoms/TruncatedText';

const completionNotificationModes = ['NONE', 'DUE_DAYS'];

const AcknowledgementSettings: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const manualQuery = useDraftManual(id);
  const [isSaving, setIsSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState('completion');
  const [completionNotificationMode, setCompletionNotificationMode] = useState(completionNotificationModes[0]);
  const [dueDays, setDueDays] = useState<number | null>(null);
  const [enableEmailNotifications, setEnableEmailNotifications] = useState(false);
  const [enablePushNotifications, setEnablePushNotifications] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<ManualGroup[]>([]);
  const [groupSearch, setGroupSearch] = useState<string>('');
  const [groupRef, groupInView] = useInView();
  const manualGroupsQuery = useIssuedManualGroups(id || '0', { search: groupSearch, size: 100 }, { enabled: !!id });
  const { update: updateSettings } = useManageAcknowledgementSettings();

  React.useEffect(() => {
    if (groupInView) {
      manualGroupsQuery.fetchNextPage();
    }
  }, [groupInView, manualGroupsQuery]);

  const groupsTrigger = manualGroupsQuery.hasNextPage ? (
    <Stack
      key='groups-trigger'
      ref={groupRef}
      direction='row'
      spacing={4}
    >
      <Button
        onClick={() => manualGroupsQuery.fetchNextPage()}
        disabled={!manualGroupsQuery.hasNextPage || manualGroupsQuery.isFetchingNextPage}
      >
        {manualGroupsQuery.hasNextPage ? t('common.loadMore') : ''}
      </Button>
    </Stack>
  ) : null;

  const manualGroups = useMemo(() => {
    let result =
      manualGroupsQuery.data && manualGroupsQuery.data.pages
        ? manualGroupsQuery.data.pages.flatMap((x) => x.content)
        : [];
    return result;
  }, [manualGroupsQuery.data]);

  const acknowledgementSettingsQuery = useAcknowledgementSettings(id, {
    onSuccess: (data: AcknowledgmentSettingsType) => {
      if (!!data.dueDays) setCompletionNotificationMode('DUE_DAYS');
      setDueDays(data.dueDays || null);
      setEnableEmailNotifications(data.emailNotifications ?? false);
      setEnablePushNotifications(data.pushNotifications ?? false);
    },
  });

  const selectTab = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  useEffect(() => {
    if (acknowledgementSettingsQuery.data && manualGroups) {
      if (acknowledgementSettingsQuery.data.groups.length > 0) {
        setSelectedGroups(manualGroups.filter((g) => acknowledgementSettingsQuery.data.groups.includes(g.id)));
      }
    }
  }, [acknowledgementSettingsQuery.data, manualGroups]);

  useEffect(() => {
    const data = acknowledgementSettingsQuery.data;
    if (data) {
      if (!!data.dueDays) setCompletionNotificationMode('DUE_DAYS');
      setDueDays(data.dueDays || null);
      setEnableEmailNotifications(data.emailNotifications ?? false);
      setEnablePushNotifications(data.pushNotifications ?? false);
    }
  }, [acknowledgementSettingsQuery.data]);

  const handleSaveClick = () => {
    setIsSaving(true);
    const dueDaysToSend = completionNotificationMode === 'NONE' ? null : dueDays;
    const data: AcknowledgmentSettingsType = {
      manualId: id!,
      groups: selectedGroups.map((x) => x.id),
      dueDays: dueDaysToSend ?? undefined,
      emailNotifications: enableEmailNotifications,
      pushNotifications: enablePushNotifications,
    };
    updateSettings(
      id!,
      data,
      (jobId) => {
        setIsSaving(false);
        queryClient.invalidateQueries(['acknowledgement_settings', id]);
        navigate('/admin/policy/draft-manuals');
        enqueueSnackbar(t('draftManuals.acknowledgementSettings.successMessage'), { variant: 'success' });
      },
      (errors) => {
        setIsSaving(false);
        enqueueSnackbar(errors.message, {
          variant: 'error',
          autoHideDuration: 20000,
        });
      }
    );
  };

  const pageTitle = manualQuery.data ? parse(manualQuery.data?.name ?? '') : '';

  return (
    <TakeoverLayout
      pageTitle={pageTitle}
      backUrl='/admin/policy/draft-manuals'
    >
      {acknowledgementSettingsQuery.isLoading || acknowledgementSettingsQuery.isFetching ? (
        <Loading />
      ) : (
        <Card
          sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
          className='lex-form'
        >
          <Form onSubmit={handleSaveClick}>
            <CardContent>
              <Tabs
                value={currentTab}
                onChange={selectTab}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  mb: 3,
                }}
              >
                <Tab
                  label={t('draftManuals.acknowledgementSettings.tabs.completion')}
                  value='completion'
                />
                <Tab
                  label={t('draftManuals.acknowledgementSettings.tabs.notifications')}
                  value='notifications'
                />
                <Tab
                  label={t('draftManuals.acknowledgementSettings.tabs.groups')}
                  value='groups'
                />
              </Tabs>
              <Box
                className='completion-settings'
                sx={{
                  display: currentTab === 'completion' ? 'block' : 'none',
                }}
              >
                <RadioGroup
                  value={completionNotificationMode}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const newMode = (event.target as HTMLInputElement).value;
                    setCompletionNotificationMode(newMode);
                  }}
                  sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}
                >
                  {completionNotificationModes.map((mode) => (
                    <Box
                      className={`mode-${mode}`}
                      key={`mode_${mode}`}
                      sx={{
                        border: `solid 1px ${neutral[200]}`,
                        borderRadius: 2,
                        p: 3,
                        '&:has(input:checked)': {
                          borderColor: success[400],
                        },
                      }}
                    >
                      <FormControlLabel
                        value={mode}
                        control={<Radio />}
                        label={
                          <Stack>
                            <Typography fontWeight={600}>
                              {t(`draftManuals.acknowledgementSettings.completionNotificationMode.${mode}.label`)}
                            </Typography>
                            <Typography variant='caption'>
                              {t(`draftManuals.acknowledgementSettings.completionNotificationMode.${mode}.hint`)}
                            </Typography>
                            {mode === 'DUE_DAYS' && (
                              <Stack direction='row'>
                                <TextField
                                  label={false}
                                  size='small'
                                  variant='outlined'
                                  value={dueDays?.toString() || ''}
                                  onChange={({ target }) => setDueDays(Number(target.value))}
                                  sx={{ minWidth: 100, p: 0, mt: 1 }}
                                  disabled={completionNotificationMode !== 'DUE_DAYS'}
                                />
                              </Stack>
                            )}
                          </Stack>
                        }
                      />
                    </Box>
                  ))}
                </RadioGroup>
              </Box>
              <Box
                className='notifications-settings'
                sx={{
                  display: currentTab === 'notifications' ? 'block' : 'none',
                }}
              >
                <Stack spacing={2}>
                  <Box
                    className='email-notifications'
                    sx={{
                      border: `solid 1px ${neutral[200]}`,
                      borderRadius: 2,
                      p: 3,
                      '&:has(input:checked)': {
                        borderColor: success[400],
                      },
                    }}
                  >
                    <FormControlLabel
                      value={enableEmailNotifications}
                      control={
                        <Checkbox
                          checked={enableEmailNotifications}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setEnableEmailNotifications(event.target.checked)
                          }
                        />
                      }
                      label={
                        <Stack>
                          <Typography fontWeight={600}>
                            {t(`draftManuals.acknowledgementSettings.emailNotifications.label`)}
                          </Typography>
                          <Typography variant='caption'>
                            {t(`draftManuals.acknowledgementSettings.emailNotifications.hint`)}
                          </Typography>
                        </Stack>
                      }
                    />
                  </Box>
                  <Box
                    className='push-notifications'
                    sx={{
                      border: `solid 1px ${neutral[200]}`,
                      borderRadius: 2,
                      p: 3,
                      '&:has(input:checked)': {
                        borderColor: success[400],
                      },
                    }}
                  >
                    <FormControlLabel
                      value={enablePushNotifications}
                      control={
                        <Checkbox
                          checked={enablePushNotifications}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setEnablePushNotifications(event.target.checked)
                          }
                        />
                      }
                      label={
                        <Stack>
                          <Typography fontWeight={600}>
                            {t(`draftManuals.acknowledgementSettings.pushNotifications.label`)}
                          </Typography>
                          <Typography variant='caption'>
                            {t(`draftManuals.acknowledgementSettings.pushNotifications.hint`)}
                          </Typography>
                        </Stack>
                      }
                    />
                  </Box>
                </Stack>
              </Box>
              <Box
                className='groups-settings'
                sx={{
                  display: currentTab === 'groups' ? 'block' : 'none',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 4,
                  }}
                >
                  <Card>
                    <CardContent>
                      <Stack spacing={2}>
                        <Typography variant='h6'>Groups With Access</Typography>
                        <QuickSearch
                          id='groups_search'
                          initialValue={groupSearch}
                          onChange={(val) => setGroupSearch(val)}
                          placeholder={t('policy.acknowledgements.groups.searchPlaceholder')}
                        />
                        <Box
                          sx={{
                            maxHeight: '50vh',
                            overflow: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'auto 1fr',
                              gap: 2,
                              alignItems: 'center',
                            }}
                          >
                            {manualGroups.length > 0 ? (
                              <>
                                <Fragment key={`groups_all`}>
                                  <Checkbox
                                    checked={selectedGroups.length === manualGroups.length}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                      if (event.target.checked) {
                                        setSelectedGroups(manualGroups);
                                      } else {
                                        setSelectedGroups([]);
                                      }
                                    }}
                                    value={[]}
                                    name='selected_groups'
                                    sx={{
                                      p: 0,
                                    }}
                                  />
                                  <Stack>
                                    <Typography sx={{ fontWeight: 600 }}>All groups</Typography>
                                  </Stack>
                                </Fragment>
                                {manualGroups.map((group) => (
                                  <Fragment key={`groups_${group.id}`}>
                                    <Checkbox
                                      checked={selectedGroups.filter((x) => x.id === group.id).length > 0}
                                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        if (event.target.checked) {
                                          setSelectedGroups((prev) => [...prev, group]);
                                        } else {
                                          setSelectedGroups((prev) => prev.filter((x) => x.id !== group.id));
                                        }
                                      }}
                                      value={group.id}
                                      name='selected_groups'
                                      sx={{
                                        p: 0,
                                      }}
                                    />
                                    <Stack>
                                      <TruncatedText
                                        text={group.name}
                                        maxWidth={260}
                                        typographyProps={{ sx: { fontWeight: 600 } }}
                                      />
                                      <Typography variant='caption'>{group.description}</Typography>
                                    </Stack>
                                  </Fragment>
                                ))}
                                {groupsTrigger}
                              </>
                            ) : (
                              <Typography>No groups found</Typography>
                            )}
                          </Box>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Stack spacing={2}>
                        <Typography variant='h6'>Selected Groups</Typography>
                        {selectedGroups.length > 0 ? (
                          <Box
                            sx={{
                              maxHeight: '50vh',
                              overflow: 'auto',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                gap: 2,
                                alignItems: 'center',
                              }}
                            >
                              {selectedGroups.map((group) => (
                                <Fragment key={`selected_groups_${group.id}`}>
                                  <IconButton
                                    onClick={() => setSelectedGroups((prev) => prev.filter((x) => x.id !== group.id))}
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                  <Stack>
                                    <TruncatedText
                                      text={group.name}
                                      maxWidth={260}
                                      typographyProps={{ sx: { fontWeight: 600 } }}
                                    />
                                    <Typography variant='caption'>{group.description}</Typography>
                                  </Stack>
                                </Fragment>
                              ))}
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              minHeight: '40vh',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Stack
                              sx={{
                                textAlign: 'center',
                              }}
                            >
                              <img
                                src='/admin/img/policy/acknowledgements/empty_groups.png'
                                alt={t('policy.acknowledgements.groups.empty.title')}
                              />
                              <Typography variant='subtitle1'>
                                {t('policy.acknowledgements.groups.empty.title')}
                              </Typography>
                              <Typography variant='body2'>
                                {t('policy.acknowledgements.groups.empty.subtitle')}
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Box />
              <Stack
                direction='row'
                spacing={2}
              >
                <Button
                  color='neutral'
                  onClick={() => navigate('/admin/policy/draft-manuals')}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleSaveClick}
                  disabled={isSaving}
                  component='button'
                  type='submit'
                  variant='contained'
                >
                  {t('draftManuals.acknowledgementSettings.save')}
                </Button>
              </Stack>
            </CardActions>
          </Form>
        </Card>
      )}
    </TakeoverLayout>
  );
};

export default AcknowledgementSettings;
